import type { Card } from "../data/types";
import {
  getTaskMeta,
  getPhaseSortValue,
  sortCardsByExecution,
  type TaskType,
  type Phase,
} from "./taskMeta";

export interface PhaseBlock {
  phase: Phase | "unknown";
  cards: Card[];
}

export type TimelineTab = "one_off" | "recurring_daily" | "recurring_weekly";

/**
 * Groups cards into a two-level structure: first by task-type tab, then by phase.
 * Returns a record where each key is a task type and each value is an array of phase blocks
 * sorted by phase order with cards sorted by execution order.
 */
export function groupTimelineCards(cards: Card[]): Record<TimelineTab, PhaseBlock[]> {
  const result: Record<TimelineTab, PhaseBlock[]> = {
    one_off: [],
    recurring_daily: [],
    recurring_weekly: [],
  };

  const tabs: TimelineTab[] = ["one_off", "recurring_daily", "recurring_weekly"];

  for (const tab of tabs) {
    result[tab] = groupByPhase(cards, tab);
  }

  return result;
}

/**
 * Filters cards by the given tab type, groups by phase, and returns sorted PhaseBlocks.
 */
export function groupByPhase(cards: Card[], tab: TimelineTab): PhaseBlock[] {
  const filtered = cards.filter((card) => {
    const taskType = getTaskMeta(card).taskType;
    if (taskType === "unknown") {
      // Unknown tasks with a target date fall into one_off tab
      return tab === "one_off" && Boolean(card.targetDate);
    }
    return taskType === tab;
  });

  const sorted = sortCardsByExecution(filtered);

  const groups = new Map<string, Card[]>();
  for (const card of sorted) {
    const phase = getTaskMeta(card).phase;
    const bucket = groups.get(phase) ?? [];
    bucket.push(card);
    groups.set(phase, bucket);
  }

  const blocks: PhaseBlock[] = [];
  for (const [phase, phaseCards] of groups.entries()) {
    blocks.push({ phase: phase as Phase | "unknown", cards: phaseCards });
  }

  return blocks.sort(
    (a, b) => getPhaseSortValue(a.phase as Phase) - getPhaseSortValue(b.phase as Phase),
  );
}

/**
 * Returns cards for a specific tab type (flat, sorted by execution).
 */
export function getTimelineTabCards(cards: Card[], tab: TimelineTab): Card[] {
  const filtered = cards.filter((card) => {
    const taskType = getTaskMeta(card).taskType;
    if (taskType === "unknown") {
      return tab === "one_off" && Boolean(card.targetDate);
    }
    return taskType === tab;
  });

  return sortCardsByExecution(filtered);
}
